import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, X, ChevronDown, Phone } from 'lucide-react';
import SEO from '../components/SEO';

const ForBusinesses = () => {
  const [coastersAnimated, setCoastersAnimated] = useState(false);
  const coasterShowcaseRef = useRef(null);

  // Scroll reveal animation
  useEffect(() => {
    const handleScroll = () => {
      document.querySelectorAll('.reveal').forEach(el => {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight - 100) {
          el.classList.add('active');
        }
      });
      
      // Coaster animation trigger
      if (coasterShowcaseRef.current && !coastersAnimated) {
        const rect = coasterShowcaseRef.current.getBoundingClientRect();
        if (rect.top < window.innerHeight - 100) {
          setCoastersAnimated(true);
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, [coastersAnimated]);

  return (
    <div className="bg-[#FEF7ED] text-[#1e1b4b]">
      <SEO 
        title="Advertise Your Business"
        description="Reach local customers at the perfect moment with Tappd's QR coaster advertising. Two simple plans, from $125/month, at the bars and restaurants your customers already visit."
        url="/for-businesses"
        keywords="business advertising, local marketing, QR code advertising, coaster marketing, small business marketing, Kansas City advertising"
      />
      <style>{`
        .font-display { font-family: 'Faustina', Georgia, serif; }
        .font-body { font-family: 'DM Sans', sans-serif; }
        .font-mono { font-family: 'JetBrains Mono', monospace; }
        
        .gradient-text {
          background: linear-gradient(135deg, #ea580c 0%, #f97316 50%, #ea580c 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(-3deg); }
          50% { transform: translateY(-15px) rotate(3deg); }
        }
        .float { animation: float 5s ease-in-out infinite; }
        
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 30px rgba(249, 115, 22, 0.3); }
          50% { box-shadow: 0 0 50px rgba(249, 115, 22, 0.5); }
        }
        .pulse-glow { animation: pulse-glow 3s ease-in-out infinite; }
        
        @keyframes pulse-glow-img {
          0%, 100% { filter: drop-shadow(0 0 20px rgba(249, 115, 22, 0.3)); }
          50% { filter: drop-shadow(0 0 35px rgba(249, 115, 22, 0.5)); }
        }
        .pulse-glow-img { animation: pulse-glow-img 3s ease-in-out infinite; }
        
        .hover-lift {
          transition: transform 0.4s ease, box-shadow 0.4s ease;
        }
        .hover-lift:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 60px rgba(234, 88, 12, 0.15);
        }
        
        .reveal {
          opacity: 0;
          transform: translateY(40px);
          transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .reveal.active {
          opacity: 1;
          transform: translateY(0);
        }
        
        @keyframes float-1 {
          0%, 100% { transform: rotate(-15deg) translateY(0); }
          50% { transform: rotate(-15deg) translateY(-12px); }
        }
        @keyframes float-2 {
          0%, 100% { transform: rotate(10deg) translateY(0); }
          50% { transform: rotate(10deg) translateY(-14px); }
        }
        @keyframes float-3 {
          0%, 100% { transform: rotate(-5deg) translateY(0); }
          50% { transform: rotate(-5deg) translateY(-10px); }
        }
        
        .coaster-float-1 { animation: float-1 4s ease-in-out infinite; }
        .coaster-float-2 { animation: float-2 4.5s ease-in-out infinite; }
        .coaster-float-3 { animation: float-3 5s ease-in-out infinite; }
        
        details summary::-webkit-details-marker { display: none; }
        details summary { list-style: none; }
      `}</style>

      {/* HERO SECTION */}
      <section className="relative pt-32 pb-20 lg:pt-36 lg:pb-28 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-96 h-96 bg-orange-200/40 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-amber-200/40 rounded-full blur-3xl"></div>
        </div>
        
        {/* V5 Gradient Line at Bottom */}
        <div className="absolute bottom-0 left-0 w-full h-1 z-20" style={{ background: 'linear-gradient(to right, #8b5cf6 0%, #f97316 100%)' }}></div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Copy */}
            <motion.div 
              className="space-y-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {/* V5 Code Accent */}
              <p className="font-mono text-xs text-[#1e1b4b]/30">// for_businesses</p>
              
              {/* Beta launch badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full shadow-lg">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                <span className="text-sm font-semibold text-white font-body">Tappd Beta Is Now LIVE</span>
              </div>
              
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#1e1b4b] leading-tight" style={{ fontWeight: 500 }}>
                Reach customers when they're <span className="gradient-text">deciding what to do next</span>
              </h1>
              
              <p className="text-xl text-[#1e1b4b]/60 leading-relaxed font-body">
                Your ad sits on the table while people plan their evening. 
                "What should we do after this?" They scan. They find you — and only you in your category.
              </p>
              
              {/* Key stats */}
              <div className="flex flex-wrap gap-6">
              </div>
              
              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/pricing" 
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full text-lg font-semibold text-white shadow-xl shadow-orange-500/30 hover:shadow-orange-500/50 transition-all"
                >
                  See Plans
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <a 
                  href="#how-it-works" 
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-[#1e1b4b]/20 rounded-full text-lg font-semibold text-[#1e1b4b] hover:bg-white/50 transition-all"
                >
                  See How It Works
                </a>
              </div>
            </motion.div>
            
            {/* Visual: Coaster mockup */}
            <motion.div 
              className="relative flex justify-center lg:justify-end"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="relative">
                {/* Main coaster */}
                <div className="w-72 h-72 md:w-80 md:h-80 rounded-full bg-gradient-to-br from-orange-100 to-amber-100 border-4 border-white shadow-2xl flex items-center justify-center float pulse-glow">
                  <div className="text-center p-8">
                    <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <svg className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                      </svg>
                    </div>
                    <div className="font-display text-2xl text-[#1e1b4b] mb-1">tappd</div>
                    <div className="text-sm text-[#1e1b4b]/60 mb-4 font-body">Scan for local deals</div>
                    <div className="w-16 h-16 bg-white rounded-lg mx-auto p-2 shadow-inner">
                      <div className="w-full h-full grid grid-cols-4 gap-0.5">
                        {[...Array(16)].map((_, i) => (
                          <div key={i} className={`${[0,1,3,4,6,7,10,11,12,13,15].includes(i) ? 'bg-[#1e1b4b]' : 'bg-transparent'} rounded-sm`}></div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Floating deal cards */}
                <div className="absolute -top-4 -right-8 bg-white rounded-xl p-3 shadow-xl border border-orange-100" style={{ animation: 'float 4s ease-in-out infinite', animationDelay: '-1s' }}>
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center text-lg">🍕</div>
                    <div>
                      <div className="text-xs font-semibold text-[#1e1b4b] font-body">Angelo's Pizza</div>
                      <div className="text-xs text-green-600 font-body">+12 customers today</div>
                    </div>
                  </div>
                </div>
                
                <div className="absolute -bottom-4 -left-8 bg-white rounded-xl p-3 shadow-xl border border-orange-100" style={{ animation: 'float 4s ease-in-out infinite', animationDelay: '-2s' }}>
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-lg">💇</div>
                    <div>
                      <div className="text-xs font-semibold text-[#1e1b4b] font-body">Classic Cuts</div>
                      <div className="text-xs text-green-600 font-body">+8 customers today</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* THE PROBLEM: Why Old Ads Fail */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16 reveal">
            <h2 className="font-display text-3xl md:text-4xl text-[#1e1b4b] mb-4">
              Traditional ads are <span className="line-through text-[#1e1b4b]/40">expensive</span> and easy to ignore
            </h2>
            <p className="text-lg text-[#1e1b4b]/60 font-body">Here's the problem with where you're probably spending money:</p>
          </div>
          
          {/* Comparison cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-12 reveal">
            {/* Facebook */}
            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </div>
                <div>
                  <div className="font-semibold text-[#1e1b4b] font-body">Facebook Ads</div>
                  <div className="text-sm text-[#1e1b4b]/50 font-body">Social media</div>
                </div>
              </div>
              <div className="text-4xl font-display text-[#1e1b4b]/40 mb-2">0.9%</div>
              <div className="text-sm text-[#1e1b4b]/50 font-body">average click-through rate</div>
              <div className="mt-4 text-sm text-[#1e1b4b]/60 font-body">
                People scroll past your ad in 0.25 seconds. You're competing with cat videos.
              </div>
            </div>
            
            {/* Google */}
            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-500" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                </div>
                <div>
                  <div className="font-semibold text-[#1e1b4b] font-body">Google Ads</div>
                  <div className="text-sm text-[#1e1b4b]/50 font-body">Search & display</div>
                </div>
              </div>
              <div className="text-4xl font-display text-[#1e1b4b]/40 mb-2">3.2%</div>
              <div className="text-sm text-[#1e1b4b]/50 font-body">average click-through rate</div>
              <div className="mt-4 text-sm text-[#1e1b4b]/60 font-body">
                Only works when people are actively searching. Miss everyone who isn't.
              </div>
            </div>
            
            {/* Direct Mail */}
            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gray-200 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                  </svg>
                </div>
                <div>
                  <div className="font-semibold text-[#1e1b4b] font-body">Direct Mail</div>
                  <div className="text-sm text-[#1e1b4b]/50 font-body">Postcards & flyers</div>
                </div>
              </div>
              <div className="text-4xl font-display text-[#1e1b4b]/40 mb-2">1.0%</div>
              <div className="text-sm text-[#1e1b4b]/50 font-body">average response rate</div>
              <div className="mt-4 text-sm text-[#1e1b4b]/60 font-body">
                Goes straight in the trash. No way to track if it even worked.
              </div>
            </div>
          </div>
          
          {/* The insight */}
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-2xl p-8 border border-orange-200 text-center reveal">
            <div className="text-lg text-[#1e1b4b]/70 mb-2 font-body">The problem isn't your offer. It's the timing.</div>
            <div className="font-display text-2xl text-[#1e1b4b]">
              You're reaching people when they're <span className="gradient-text">not ready to act</span>.
            </div>
          </div>
        </div>
      </section>

      {/* THE SOLUTION: The Coaster Moment */}
      <section className="py-20 bg-gradient-to-b from-[#1e1b4b] to-[#2d2a5e] text-white" id="how-it-works">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Copy */}
            <div className="space-y-8 reveal">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded-full">
                <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
                <span className="text-sm font-semibold text-orange-300 font-body">The Tappd Difference</span>
              </div>
              
              <h2 className="font-display text-3xl md:text-5xl leading-tight">
                Catch them in <span className="text-orange-400">The Coaster Moment</span>
              </h2>
              
              <p className="text-xl text-white/60 leading-relaxed font-body">
                Picture this: A group sits at a bar. Between drinks, someone asks: 
                <span className="text-white font-medium"> "What should we do after this?"</span>
              </p>
              
              <p className="text-lg text-white/60 leading-relaxed font-body">
                Someone notices the coaster. Scans it. Suddenly they're browsing local deals — 
                and your business is right there, offering exactly what they need.
              </p>
              
              {/* The comparison */}
              <div className="bg-white/10 rounded-2xl p-6 border border-white/10">
                <div className="text-sm text-white/50 mb-4 font-body">Engagement rate comparison</div>
                
                {/* Bar chart */}
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1 font-body">
                      <span className="text-white/70">Facebook Ads</span>
                      <span className="text-white/50">0.9%</span>
                    </div>
                    <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-white/30 rounded-full" style={{ width: '7%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1 font-body">
                      <span className="text-white/70">Google Ads</span>
                      <span className="text-white/50">3.2%</span>
                    </div>
                    <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-white/30 rounded-full" style={{ width: '26%' }}></div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4 text-center">
                  <span className="text-orange-400 font-bold text-2xl"></span>
                  <span className="text-white/60 text-sm ml-2 font-body">higher than Facebook</span>
                </div>
              </div>
            </div>
            
            {/* Visual: Scene illustration */}
            <div className="relative reveal">
              <div className="bg-white/10 rounded-3xl p-8 border border-white/10">
                {/* Bar scene */}
                <img 
                  src="https://images.unsplash.com/photo-1543007630-9710e4a00a20?w=600&q=80" 
                  alt="Friends at bar" 
                  className="w-full aspect-[4/3] object-cover rounded-2xl mb-6"
                />
                
                {/* Speech bubble */}
                <div className="bg-white rounded-2xl p-4 shadow-xl relative">
                  <div className="absolute -top-3 left-8 w-6 h-6 bg-white transform rotate-45"></div>
                  <div className="relative z-10">
                    <div className="text-[#1e1b4b] font-medium mb-2 font-body">"What should we do after this?"</div>
                    <div className="text-[#1e1b4b]/60 text-sm font-body">*scans coaster*</div>
                    <div className="mt-3 flex items-center gap-2">
                      <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center text-sm">🍕</div>
                      <div>
                        <div className="text-xs text-orange-600 font-semibold font-body">Angelo's Pizza</div>
                        <div className="text-xs text-[#1e1b4b]/50 font-body">Free appetizer with any pie</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Checkmarks */}
              <div className="absolute -bottom-6 left-0 right-0 flex justify-center gap-4">
                <div className="bg-white rounded-full px-4 py-2 shadow-lg flex items-center gap-2 text-sm font-body">
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="text-[#1e1b4b]">No scrolling past</span>
                </div>
                <div className="bg-white rounded-full px-4 py-2 shadow-lg flex items-center gap-2 text-sm font-body">
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="text-[#1e1b4b]">Perfect timing</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS: 3 Simple Steps */}
      <section className="py-20 bg-[#FEF7ED]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16 reveal">
            <h2 className="font-display text-3xl md:text-4xl text-[#1e1b4b] mb-4">
              How it works for your business
            </h2>
            <p className="text-lg text-[#1e1b4b]/60 font-body">Three steps to start getting customers</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 reveal">
            {[
              {
                num: '1',
                icon: (
                  <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                  </svg>
                ),
                title: 'Create Your Offer',
                description: 'Pick a deal that drives action. "Free appetizer," "20% off first visit," "$10 off $50." We\'ll help you craft the perfect hook.'
              },
              {
                num: '2',
                icon: (
                  <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                  </svg>
                ),
                title: 'We Place Coasters',
                description: 'We distribute Tappd coasters to bars, breweries, and pubs within 15 miles of your business. You pick the locations.'
              },
              {
                num: '3',
                icon: (
                  <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                  </svg>
                ),
                title: 'Watch Them Come In',
                description: 'Track every scan, claim, and redemption in real-time. See exactly how many customers you\'re getting and from which venues.'
              }
            ].map((step, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-8 shadow-lg border border-orange-100 relative">
                <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center text-white font-display text-xl shadow-lg">{step.num}</div>
                <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mb-6">
                  {step.icon}
                </div>
                <h3 className="font-display text-xl text-[#1e1b4b] mb-3">{step.title}</h3>
                <p className="text-[#1e1b4b]/60 font-body">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THE COASTERS: Physical Product */}
      <section className="py-24 bg-white overflow-hidden" id="coasters">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Copy */}
            <div className="space-y-8 reveal">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 rounded-full">
                <span className="text-sm font-semibold text-orange-700 font-body">Premium Quality</span>
              </div>
              
              <h2 className="font-display text-4xl md:text-5xl text-[#1e1b4b] leading-tight">
                Your deal on <span className="gradient-text">every table</span>
              </h2>
              
              <p className="text-xl text-[#1e1b4b]/60 leading-relaxed font-body">
                Our coasters aren't cheap cardboard throwaways. They're premium, full-color printed coasters 
                that bars actually want to use. Your deal gets prime real estate — right under their drink.
              </p>
              
              <div className="space-y-4">
                {[
                  { title: 'Seasonal designs', desc: 'Fresh designs every season keep the coasters interesting and bars happy to display them.' },
                  { title: '50+ venue network', desc: 'Your deal appears across our growing network of local bars, breweries, and pubs.' },
                  { title: 'QR code scanning', desc: 'Customers scan with any phone camera — no app download required.' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Check className="w-6 h-6 text-orange-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#1e1b4b] mb-1 font-body">{item.title}</h4>
                      <p className="text-[#1e1b4b]/60 text-sm font-body">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Coaster Visual */}
            <div ref={coasterShowcaseRef} className="relative flex justify-center" id="coaster-showcase">
              <div className="relative w-[280px] h-[280px] sm:w-[340px] sm:h-[340px] md:w-[420px] md:h-[420px] lg:w-[480px] lg:h-[480px]">
                
                {/* Back coaster */}
                <div className={`absolute top-8 -left-4 w-56 h-56 md:w-72 md:h-72 lg:w-80 lg:h-80 ${coastersAnimated ? 'coaster-float-1 opacity-100' : 'opacity-0'}`} style={{ transform: 'rotate(-15deg)', transition: 'opacity 0.5s' }}>
                  <img src="https://tappd-uploads.nyc3.cdn.digitaloceanspaces.com/coaster_designs/tappd-coaster-8a-community.png" alt="Tappd Coaster Design" className="w-full h-full object-contain drop-shadow-2xl"/>
                </div>

                {/* Front coaster */}
                <div className={`absolute -top-4 right-0 w-56 h-56 md:w-72 md:h-72 lg:w-80 lg:h-80 pulse-glow-img ${coastersAnimated ? 'coaster-float-2 opacity-100' : 'opacity-0'}`} style={{ transform: 'rotate(10deg)', transition: 'opacity 0.5s 0.2s' }}>
                  <img src="https://tappd-uploads.nyc3.cdn.digitaloceanspaces.com/coaster_designs/tappd-coaster-1c-value.png" alt="Tappd Coaster Design" className="w-full h-full object-contain drop-shadow-2xl"/>
                </div>

                {/* Bottom coaster */}
                <div className={`absolute bottom-0 left-8 w-48 h-48 md:w-64 md:h-64 lg:w-72 lg:h-72 ${coastersAnimated ? 'coaster-float-3 opacity-100' : 'opacity-0'}`} style={{ transform: 'rotate(-5deg)', transition: 'opacity 0.5s 0.4s' }}>
                  <img src="https://tappd-uploads.nyc3.cdn.digitaloceanspaces.com/coaster_designs/tappd-coaster-8e-community.png" alt="Tappd Coaster Design" className="w-full h-full object-contain drop-shadow-xl"/>
                </div>
                
                {/* Scan count badge */}
                <div className={`absolute top-0 right-8 bg-white rounded-xl px-4 py-3 shadow-xl border border-orange-100 z-10 ${coastersAnimated ? 'opacity-100' : 'opacity-0'}`} style={{ transition: 'opacity 0.6s ease 0.8s' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"/>
                      </svg>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-[#1e1b4b] font-body">2,847</div>
                      <div className="text-xs text-[#1e1b4b]/50 font-body">scans this month</div>
                    </div>
                  </div>
                </div>
                
                {/* Venue badge */}
                <div className={`absolute -bottom-2 right-4 bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl px-4 py-3 shadow-xl z-10 ${coastersAnimated ? 'opacity-100' : 'opacity-0'}`} style={{ transition: 'opacity 0.6s ease 0.8s' }}>
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                    </svg>
                    <div>
                      <div className="text-white font-bold font-body">50+ Venues</div>
                      <div className="text-white/70 text-xs font-body">& growing weekly</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PERFECT FOR: Business Types */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16 reveal">
            <h2 className="font-display text-3xl md:text-4xl text-[#1e1b4b] mb-4">
              Perfect for local businesses like yours
            </h2>
            <p className="text-lg text-[#1e1b4b]/60 font-body">If you serve the local community, Tappd works for you</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 reveal">
            {[
              { emoji: '🐕', name: 'Pet Services' },
              { emoji: '💇', name: 'Salons & Spas' },
              { emoji: '🚗', name: 'Auto Services' },
              { emoji: '🏋️', name: 'Fitness Studios' },
              { emoji: '🎯', name: 'Entertainment' },
              { emoji: '🛍️', name: 'Retail Shops' },
              { emoji: '🔧', name: 'Home Services' },
              { emoji: '💼', name: 'Professional Services' }
            ].map((type, idx) => (
              <div key={idx} className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-6 text-center border border-orange-100 hover:shadow-lg transition-shadow">
                <div className="text-3xl mb-3">{type.emoji}</div>
                <div className="font-semibold text-[#1e1b4b] font-body">{type.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-[#FEF7ED]">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12 reveal">
            <h2 className="font-display text-3xl md:text-4xl text-[#1e1b4b] mb-4">
              Frequently asked questions
            </h2>
          </div>
          
          <div className="space-y-4 reveal">
            {[
              { q: 'How long until I see results?', a: "Your deal is live at your chosen venues as soon as your subscription is active and our team has reviewed your listing — usually within one business day." },
              { q: 'Can I change my deal?', a: "Yes! You can update your offer anytime through your dashboard. Seasonal deals, limited-time promotions, whatever works for your business." },
              { q: 'Which bars will feature my deal?', a: "You choose. During setup you pick from the partner venues near your business — one venue on the Local plan, up to three on Multi." },
              { q: 'How do I know it\'s working?', a: "Your dashboard shows how many people claimed your deal and how many redeemed it at your counter, plus every lead captured." },
              { q: "Is there a contract?", a: "No. Monthly plans renew month to month and annual plans include two months free. Change plan or cancel anytime from the billing portal." },
            ].map((faq, idx) => (
              <details key={idx} className="bg-white rounded-2xl border border-orange-100 group">
                <summary className="p-6 cursor-pointer flex items-center justify-between font-semibold text-[#1e1b4b] hover:text-orange-600 transition-colors font-body">
                  {faq.q}
                  <ChevronDown className="w-5 h-5 text-orange-400 group-open:rotate-180 transition-transform" />
                </summary>
                <div className="px-6 pb-6 text-[#1e1b4b]/60 font-body">{faq.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 bg-gradient-to-br from-[#1e1b4b] to-[#2d2a5e] text-white relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1514933651103-005eec06c04b?w=1920" alt="" className="w-full h-full object-cover opacity-20"/>
        </div>
        
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10 reveal">
          <h2 className="font-display text-4xl md:text-5xl mb-6">
            Ready to reach more <span className="text-orange-400">local customers?</span>
          </h2>
          <p className="text-xl text-white/60 max-w-2xl mx-auto mb-10 font-body">
            Pick a plan, add your deal, and be on the table this week.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Link to="/pricing" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full text-lg font-semibold shadow-xl shadow-orange-500/30 hover:shadow-orange-500/50 transition-all">
              See Plans
              <ArrowRight className="w-5 h-5" />
            </Link>
            <button type="button" onClick={() => setIsContactModalOpen(true)} className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-white/30 rounded-full text-lg font-semibold hover:bg-white/10 transition-all">
              <Phone className="w-5 h-5" />
              Talk to Sales
            </button>
          </div>
          
          {/* Trust badges */}
          <div className="flex flex-wrap justify-center gap-6 text-white/50 text-sm font-body">
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-400" />
              Plans from $125/mo
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-400" />
              No contracts
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ForBusinesses;
